import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { DsIconComponent, type DsIconSize } from './ds-icon.component';
import { DsIconName } from './ds-icons';

export type DsIconBadgeShadow = 'none' | 'l1' | 'l2' | 'l3';
export type DsIconBadgeSize = 32 | 36 | 40 | 44 | 48 | 52 | 56 | 60;

@Component({
  selector: 'ds-icon-badge',
  standalone: true,
  imports: [DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span
      [class]="hostClasses"
      [style.background]="backgroundColor"
      [style.color]="iconColor"
    >
      <ds-icon
        [name]="name"
        [size]="iconSize"
        [fill]="fill"
        [ariaLabel]="ariaLabel"
      />
    </span>
  `,
  styles: [`
    :host {
      display: inline-flex;
      flex: 0 0 auto;
      line-height: 0;
    }

    .ds-icon-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      padding: var(--ds-icon-badge-padding);
      border-radius: var(--ds-icon-badge-border-radius);
      background: var(--ds-icon-badge-background);
      color: var(--ds-icon-badge-foreground);
      box-sizing: border-box;
    }

    .ds-icon-badge--size-32 { width: var(--ds-icon-badge-size32); height: var(--ds-icon-badge-size32); }
    .ds-icon-badge--size-36 { width: var(--ds-icon-badge-size36); height: var(--ds-icon-badge-size36); }
    .ds-icon-badge--size-40 { width: var(--ds-icon-badge-size40); height: var(--ds-icon-badge-size40); }
    .ds-icon-badge--size-44 { width: var(--ds-icon-badge-size44); height: var(--ds-icon-badge-size44); }
    .ds-icon-badge--size-48 { width: var(--ds-icon-badge-size48); height: var(--ds-icon-badge-size48); }
    .ds-icon-badge--size-52 { width: var(--ds-icon-badge-size52); height: var(--ds-icon-badge-size52); }
    .ds-icon-badge--size-56 { width: var(--ds-icon-badge-size56); height: var(--ds-icon-badge-size56); }
    .ds-icon-badge--size-60 { width: var(--ds-icon-badge-size60); height: var(--ds-icon-badge-size60); }

    .ds-icon-badge--shadow-none {
      box-shadow: none;
    }

    .ds-icon-badge--shadow-l1 {
      box-shadow: var(--ds-icon-badge-shadow-l1);
    }

    .ds-icon-badge--shadow-l2 {
      box-shadow: var(--ds-icon-badge-shadow-l2);
    }

    .ds-icon-badge--shadow-l3 {
      box-shadow: var(--ds-icon-badge-shadow-l3);
    }
  `],
})
export class DsIconBadgeComponent {
  @Input() name: DsIconName | string = 'VisibilityFill';
  @Input() size: DsIconBadgeSize = 32;
  @Input() fill = false;
  @Input() ariaLabel = '';
  @Input() backgroundColor = 'var(--ds-icon-badge-background)';
  @Input() iconColor = 'var(--ds-icon-badge-foreground)';
  @Input() shadow: DsIconBadgeShadow = 'none';

  get hostClasses(): string {
    return `ds-icon-badge ds-icon-badge--size-${this.size} ds-icon-badge--shadow-${this.shadow}`;
  }

  get iconSize(): DsIconSize {
    return `var(--ds-icon-badge-icon-size${this.size})`;
  }
}
